import React, {
  useEffect,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import axios from "axios";
import { Table, Spin } from "antd";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./TablaIndicador.css";
import logo_gobernacion from "../../assets/images/logo_gobernacion.png";
import logo_tangara from "../../assets/images/logo_tangara.png";
import config from "../../config"

const baseUrl = config.baseUrl;

const TablaIndicador = forwardRef(({ nombre_vista }, ref) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [exportLoading, setExportLoading] = useState(false); // Estado para controlar el spinner de exportación
  const recordsPerPage = 10;

  useImperativeHandle(ref, () => ({
    exportToExcel,
    exportToJson,
  }));

  // Función para obtener los datos de la API
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${baseUrl}/api/vistas/nombre/${nombre_vista}`
      );
      const allRecords = response.data.map((record) => ({
        ...record,
        municipio: record.municipio === "Cauca" ? "" : record.municipio,
        resultado: Number.isInteger(parseFloat(record.resultado))
          ? parseInt(record.resultado) // Mantener como entero si es un número entero
          : parseFloat(record.resultado).toFixed(2), // Formatear a dos decimales si es decimal
      }));

      setData(allRecords);
      setTotalRecords(allRecords.length);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [nombre_vista]);

  useEffect(() => {
    if (nombre_vista) {
      fetchData();
    }
  }, [fetchData, nombre_vista]);

  if (error) return <p>Error al cargar los datos: {error.message}</p>;

  // Función para obtener los filtros de las columnas
  const getColumnFilters = (key) => {
    return [...new Set(data.map((item) => item[key]))]
      .filter((value) => value)
      .map((value) => ({ text: value, value }));
  };

  // Nombres de las columnas de la tabla
  const columnNames = {
    desagregaciontematica: "Desagregación Temática",
    clasificacion: "Clasificación",
    año: "Vigencia",
    resultado: "Resultado",
    departamento: "Departamento",
    sub_region: "Subregión",
    municipio: "Municipio",
  };

  // Definición de las columnas de la tabla
  const columns = Object.keys(columnNames).map((key) => {
    const isFilteredColumn =
      key === "desagregaciontematica" ||
      key === "clasificacion" ||
      key === "año" ||
      key === "sub_region" ||
      key === "municipio";

    return {
      title: <span>{columnNames[key]}</span>,
      dataIndex: key,
      key: key,
      width: 250,
      ...(isFilteredColumn
        ? {
            filters: getColumnFilters(key),
            onFilter: (value, record) => record[key] === value,
          }
        : {}),
      ...(key === "resultado" ? { align: "center" } : {}),
    };
  });

  // Función para exportar a Excel
  const exportToExcel = async () => {
    setExportLoading(true); // Inicia el loading
    try {
      // Obtener todos los registros
      const response = await axios.get(`${baseUrl}/api/vistas/nombre/${nombre_vista}`);
      const allRecords = response.data;

      const worksheet = XLSX.utils.json_to_sheet(allRecords);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Datos");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const dataBlob = new Blob([excelBuffer], {
        type: "application/octet-stream",
      });

      const fileName = `${nombre_vista.replace(/\s+/g, "_")}_datos_tabla.xlsx`;
      saveAs(dataBlob, fileName);
    } catch (error) {
      console.error("Error al exportar a Excel", error);
    } finally {
      setExportLoading(false); // Finalizar carga
    }
  };

  // Función para exportar a JSON
  const exportToJson = async () => {
    setExportLoading(true); // Iniciar carga
    try {
      const response = await axios.get(
        `http://localhost:3000/api/vistas/nombre/${nombre_vista}`
      );
      const allRecords = response.data;

      const jsonBlob = new Blob([JSON.stringify(allRecords, null, 2)], {
        type: "application/json",
      });
      const fileName = `${nombre_vista.replace(/\s+/g, "_")}_datos_tabla.json`;
      saveAs(jsonBlob, fileName);
    } catch (error) {
      console.error("Error al exportar a JSON", error);
    } finally {
      setExportLoading(false); // Finalizar carga
    }
  };


  return (
    <div className="tabla-container">
      {exportLoading && (
        <div className="export-loading-overlay">
          <Spin size="large" />
          <span className="export-loading-text">Exportando archivo...</span>
        </div>
      )}

      {data.length > 0 && (
        <>
          <div className="Separador">
            <div className="separadorAzul"></div>
            <div className="separadorVerde"></div>
          </div>
          <div className="contenedorEncabezado">
            <div className="contenedorTitulos">
              <p className="titulo">{data[0].areatematica || data[0].areateamtica}</p>
              <p className="subtitulo">{data[0].tema}</p>
              <p className="indicador">Indicador: {data[0].nombreindicador}</p>
            </div>
            <div className="contenedorImagenes">
              <img src={logo_gobernacion}  alt="Logo Gobernación" className="logo1" />
              <img src={logo_tangara} alt="Logo Tángara" className="logo2" />
            </div>
          </div>
        </>
      )}

      <Table
        className="tabla-indicador"
        columns={columns}
        dataSource={data.map((item, index) => ({ ...item, key: index }))}
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize: recordsPerPage,
          total: totalRecords,
          onChange: (page) => setCurrentPage(page),
        }}
        scroll={{ x: "100%", y: "450px" }}
      />
    </div>
  );
});

export default TablaIndicador;
